<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import MessageSignatureMissingAlert from 'dashboard/components/widgets/conversation/MessageSignatureMissingAlert.vue';
import SignatureIndicator from 'dashboard/components/widgets/conversation/SignatureIndicator.vue';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead.vue';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import RichTextEditor from 'dashboard/components/widgets/RichTextEditor/RichTextEditor.vue';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import {
  getSignatureSource,
  isSignatureForcedByInbox,
  resolveSignature,
  SIGNATURE_SOURCES,
} from 'dashboard/helper/signature';
import { getInboxSource, inboxDisplayName } from 'dashboard/helper/inbox';
import { buildMessageVariables } from 'dashboard/helper/messageVariables';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import FileUpload from 'vue-upload-component';
import { mapGetters } from 'vuex';
import WhatsappTemplates from './WhatsappTemplates.vue';

export default {
  components: {
    Thumbnail,
    RichTextEditor,
    ReplyEmailHead,
    WhatsappTemplates,
    InboxDropdownItem,
    FileUpload,
    AttachmentPreview,
    MessageSignatureMissingAlert,
    SignatureIndicator,
  },
  mixins: [inboxMixin, fileUploadMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    const { fetchSignatureFlagFromUISettings, setSignatureFlagForInbox } =
      useUISettings();
    const v$ = useVuelidate();

    return { fetchSignatureFlagFromUISettings, setSignatureFlagForInbox, v$ };
  },
  data() {
    return {
      name: '',
      subject: '',
      message: '',
      bccEmails: '',
      ccEmails: '',
      targetInbox: {},
      whatsappTemplateSelected: false,
      attachedFiles: [],
      autoResolve: false,
    };
  },
  validations() {
    return {
      subject: {
        required: requiredIf(this.isAnEmailInbox),
      },
      message: {
        required,
      },
      targetInbox: {
        required,
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      messageSignature: 'getMessageSignature',
      getContact: 'contacts/getContact',
    }),
    sendWithSignature() {
      if (this.isPersonalSignatureForcedByInbox) {
        return true;
      }

      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    isPersonalSignatureForcedByInbox() {
      return isSignatureForcedByInbox(this.targetInbox);
    },
    signatureSource() {
      return getSignatureSource(this.targetInbox);
    },
    signatureToApply() {
      return resolveSignature({
        inbox: this.targetInbox,
        agentSignature: this.messageSignature,
        currentUser: this.currentUser,
        contact: this.resolvedContact,
      });
    },
    newMessagePayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        // Vai so o que foi escrito. A assinatura e aplicada pelo servidor no
        // momento do envio, junto com macro, automacao e agendamento; a marca
        // abaixo apenas diz que esta mensagem e uma das que a recebem.
        message: { content: this.message, apply_signature: true },
        mailSubject: this.subject,
        assigneeId: this.currentUser.id,
      };

      if (this.attachedFiles && this.attachedFiles.length) {
        payload.files = [];
        this.setAttachmentPayload(payload);
      }

      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }

      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }
      return payload;
    },
    selectedInbox: {
      get() {
        // Try both contactableInboxes and contactable_inboxes for compatibility
        const contact = this.getContact(this.contact.id);
        const inboxList =
          this.contact.contactableInboxes ||
          (contact && contact.contactable_inboxes) ||
          [];
        const targetInboxId = this.targetInbox && this.targetInbox.id;
        return (
          inboxList.find(inbox => {
            const inboxId = inbox && inbox.inbox && inbox.inbox.id;
            return inboxId && inboxId === targetInboxId;
          }) || {
            inbox: {},
          }
        );
      },
      set(value) {
        this.targetInbox = value.inbox;
      },
    },
    showNoInboxAlert() {
      if (!this.contact.contactableInboxes) {
        return false;
      }
      return this.inboxes.length === 0 && !this.uiFlags.isFetchingInboxes;
    },
    isSignatureEnabledForInbox() {
      return this.sendWithSignature;
    },
    // the alert points the agent to their profile settings, so it only makes
    // sense when the missing signature is the agent one
    shouldShowSignatureMissingAlert() {
      return (
        this.isSignatureEnabledForInbox &&
        !this.signatureToApply &&
        this.signatureSource === SIGNATURE_SOURCES.AGENT
      );
    },
    signatureToggleTooltip() {
      if (this.isPersonalSignatureForcedByInbox) {
        return this.$t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP');
      }

      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    signatureButtonColorScheme() {
      return this.sendWithSignature ? 'primary' : 'secondary';
    },
    inboxes() {
      const contact = this.getContact(this.contact.id);
      const inboxList = contact.contactable_inboxes || [];
      return inboxList.map(inbox => ({
        ...inbox.inbox,
        sourceId: inbox.source_id,
      }));
    },
    isAnEmailInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.EMAIL
      );
    },
    isAnWebWidgetInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.WEB
      );
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailInbox || this.isAnWebWidgetInbox;
    },
    hasWhatsappTemplates() {
      const selectedInbox = this.selectedInbox;
      const inbox = selectedInbox && selectedInbox.inbox;
      if (!inbox || !inbox.id) {
        return false;
      }

      const {
        channel_type: channelType,
        provider = '',
        phone_number: phoneNumber = '',
      } = inbox;

      // Only show templates for official WhatsApp channels:
      // 1. WhatsApp Cloud API (provider = 'whatsapp_cloud')
      // 2. 360 Dialog (provider = 'default')
      // 3. Twilio WhatsApp
      const isWhatsAppCloudChannel =
        channelType === 'Channel::Whatsapp' && provider === 'whatsapp_cloud';
      const is360DialogChannel =
        channelType === 'Channel::Whatsapp' && provider === 'default';
      const isTwilioWhatsApp =
        channelType === 'Channel::TwilioSms' &&
        phoneNumber &&
        phoneNumber.startsWith('whatsapp');

      const isOfficialWhatsApp =
        isWhatsAppCloudChannel || is360DialogChannel || isTwilioWhatsApp;

      if (!isOfficialWhatsApp) {
        return false;
      }

      // Get templates from store
      const inboxId = inbox.id || (this.targetInbox && this.targetInbox.id);
      if (!inboxId) {
        return false;
      }

      const templates =
        this.$store.getters['inboxes/getWhatsAppTemplates'](inboxId);
      return !!(templates && templates.length);
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    inbox() {
      return this.targetInbox;
    },
    resolvedContact() {
      return this.getContact(this.contact.id) || this.contact || {};
    },
    messageVariables() {
      return buildMessageVariables({
        contact: this.resolvedContact,
        agent: this.currentUser,
      });
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
  },
  watch: {
    targetInbox() {
      this.saveLastUsedInbox();
    },
    inboxes: {
      handler() {
        // Load last used inbox when inboxes are available
        if (
          this.inboxes.length > 0 &&
          (!this.targetInbox || !this.targetInbox.id)
        ) {
          this.loadLastUsedInbox();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadLastUsedInbox();
  },
  methods: {
    setAttachmentPayload(payload) {
      this.attachedFiles.forEach(attachment => {
        if (this.globalConfig.directUploadsEnabled) {
          payload.files.push(attachment.blobSignedId);
        } else {
          payload.files.push(attachment.resource.file);
        }
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.contact.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    prepareWhatsAppMessagePayload({ message: content, templateParams }) {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content, template_params: templateParams },
        assigneeId: this.currentUser.id,
      };
      return payload;
    },
    onFormSubmit() {
      const isFromWhatsApp = false;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.createConversation({
        payload: this.newMessagePayload,
        isFromWhatsApp,
      });
    },
    async createConversation({ payload, isFromWhatsApp }) {
      try {
        const data = await this.onSubmit(payload, isFromWhatsApp);

        // Auto-resolve conversation if option is enabled
        await this.resolveConversationAutomatically(data.id);

        const action = {
          type: 'link',
          to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
          message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        };
        this.onSuccess();
        useAlert(this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
      } catch (error) {
        if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },

    toggleWaTemplate(val) {
      this.whatsappTemplateSelected = val;
    },
    async onSendWhatsAppReply(messagePayload) {
      const isFromWhatsApp = true;
      const payload = this.prepareWhatsAppMessagePayload(messagePayload);
      await this.createConversation({ payload, isFromWhatsApp });
    },
    inboxReadableIdentifier(inbox) {
      const nome = inboxDisplayName(
        inbox,
        this.$t('CONVERSATION.SANDBOX_INBOX_LABEL')
      );

      return `${nome} (${inbox.channel_type})`;
    },
    computedInboxSource(inbox) {
      if (!inbox.channel_type) return '';
      const classByType = getInboxSource(
        inbox.channel_type,
        inbox.phone_number,
        inbox
      );
      return classByType;
    },
    toggleMessageSignature() {
      if (this.isPersonalSignatureForcedByInbox) {
        return;
      }

      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    saveLastUsedInbox() {
      if (this.targetInbox && this.targetInbox.id) {
        localStorage.setItem('lastUsedInboxId', this.targetInbox.id.toString());
      }
    },
    loadLastUsedInbox() {
      const lastUsedInboxId = localStorage.getItem('lastUsedInboxId');
      if (lastUsedInboxId && this.inboxes.length > 0) {
        const lastInbox = this.inboxes.find(
          inbox => inbox.id.toString() === lastUsedInboxId
        );
        if (lastInbox) {
          this.targetInbox = lastInbox;
        }
      }
    },
    async resolveConversationAutomatically(conversationId) {
      if (this.autoResolve) {
        try {
          await this.$store.dispatch('toggleStatus', {
            conversationId,
            status: wootConstants.STATUS_TYPE.RESOLVED,
          });
        } catch (error) {
          // Silently handle error to avoid disrupting the main flow
          console.error('Failed to auto-resolve conversation:', error);
        }
      }
    },
  },
};
</script>

<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <form class="w-full conversation--form" @submit.prevent="onFormSubmit">
    <div
      v-if="showNoInboxAlert"
      class="relative mx-0 mt-0 mb-2.5 p-2 rounded-none text-sm border border-solid border-yellow-500 dark:border-yellow-700 bg-yellow-200/60 dark:bg-yellow-200/20 text-slate-700 dark:text-yellow-400"
    >
      <p class="mb-0">
        {{ $t('NEW_CONVERSATION.NO_INBOX') }}
      </p>
    </div>
    <div v-else>
      <div class="flex flex-row gap-2">
        <div class="w-[50%]">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
          </label>
          <div
            class="multiselect-wrap--small"
            :class="{ 'has-multi-select-error': v$.targetInbox.$error }"
          >
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              close-on-select
              :options="[...inboxes]"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <InboxDropdownItem
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>
              <template slot="option" slot-scope="{ option }">
                <InboxDropdownItem
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>
          <label :class="{ error: v$.targetInbox.$error }">
            <span v-if="v$.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-[50%]">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.TO.LABEL') }}
            <div
              class="flex items-center h-[2.4735rem] rounded-sm py-1 px-2 bg-surface-sunken border border-solid border-line-faint"
            >
              <Thumbnail
                :src="contact.thumbnail"
                size="24px"
                :username="contact.name"
                :status="contact.availability_status"
              />
              <h4 class="m-0 ml-2 mr-2 text-sm text-muted">
                {{ contact.name }}
              </h4>
            </div>
          </label>
        </div>
      </div>
      <div v-if="isAnEmailInbox" class="w-full">
        <div class="w-full">
          <label :class="{ error: v$.subject.$error }">
            {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}
            <input
              v-model="subject"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
              @input="v$.subject.$touch"
            />
            <span v-if="v$.subject.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
            </span>
          </label>
        </div>
      </div>
      <div class="w-full">
        <div class="w-full">
          <div v-if="!hasWhatsappTemplates">
            <label>
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            </label>
            <ReplyEmailHead
              v-if="isAnEmailInbox"
              :cc-emails.sync="ccEmails"
              :bcc-emails.sync="bccEmails"
            />
            <div class="editor-wrap">
              <RichTextEditor
                v-model="message"
                class="message-editor"
                :class="{ editor_warning: v$.message.$error }"
                enable-variables
                :variables="messageVariables"
                :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                @blur="v$.message.$touch"
              >
                <template #footer>
                  <MessageSignatureMissingAlert
                    v-if="shouldShowSignatureMissingAlert"
                    class="!mx-0 mb-1"
                  />
                  <div
                    v-if="channelType"
                    class="flex items-center gap-2 mt-px mb-3"
                  >
                    <woot-button
                      v-tooltip.top-end="signatureToggleTooltip"
                      icon="signature"
                      :color-scheme="signatureButtonColorScheme"
                      variant="smooth"
                      size="small"
                      :title="signatureToggleTooltip"
                      :is-disabled="isPersonalSignatureForcedByInbox"
                      @click.prevent="toggleMessageSignature"
                    />
                    <SignatureIndicator
                      v-if="isSignatureEnabledForInbox && signatureToApply"
                      :signature="signatureToApply"
                      :source="signatureSource"
                      :is-forced-by-inbox="isPersonalSignatureForcedByInbox"
                    />
                  </div>
                </template>
              </RichTextEditor>
              <span v-if="v$.message.$error" class="editor-warning__message">
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
              </span>
            </div>
          </div>
          <WhatsappTemplates
            v-else
            :inbox-id="
              selectedInbox && selectedInbox.inbox && selectedInbox.inbox.id
            "
            @pickTemplate="toggleWaTemplate"
            @onSend="onSendWhatsAppReply"
          />
          <div v-if="isEmailOrWebWidgetInbox" class="flex flex-col">
            <FileUpload
              ref="uploadAttachment"
              input-id="newConversationAttachment"
              :size="4096 * 4096"
              :accept="allowedFileTypes"
              multiple
              :drop="true"
              :drop-directory="false"
              :data="{
                direct_upload_url: '/rails/active_storage/direct_uploads',
                direct_upload: true,
              }"
              @input-file="onFileUpload"
            >
              <woot-button
                class-names="button--upload"
                icon="attach"
                emoji="📎"
                color-scheme="secondary"
                variant="smooth"
                size="small"
              >
                {{ $t('NEW_CONVERSATION.FORM.ATTACHMENTS.SELECT') }}
              </woot-button>
              <span class="text-xs font-medium text-subtle ltr:ml-1 rtl:mr-1">
                {{ $t('NEW_CONVERSATION.FORM.ATTACHMENTS.HELP_TEXT') }}
              </span>
            </FileUpload>
            <div
              v-if="hasAttachments"
              class="max-h-20 overflow-y-auto mb-4 mt-1.5"
            >
              <AttachmentPreview
                class="[&>.preview-item]:dark:bg-slate-700 flex-row flex-wrap gap-x-3 gap-y-1"
                :attachments="attachedFiles"
                @removeAttachment="removeAttachment"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!hasWhatsappTemplates" class="w-full">
      <div class="flex items-center mb-4">
        <input
          id="autoResolve"
          v-model="autoResolve"
          type="checkbox"
          class="mr-2"
        />
        <label for="autoResolve" class="text-sm font-medium cursor-pointer">
          {{ $t('NEW_CONVERSATION.FORM.AUTO_RESOLVE') }}
        </label>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <button class="button clear" @click.prevent="onCancel">
          {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
        </button>
        <woot-button
          type="submit"
          :is-loading="conversationsUiFlags.isCreating"
        >
          {{ $t('NEW_CONVERSATION.FORM.SUBMIT') }}
        </woot-button>
      </div>
    </div>

    <transition v-if="isEmailOrWebWidgetInbox" name="modal-fade">
      <div
        v-show="$refs.uploadAttachment && $refs.uploadAttachment.dropActive"
        class="absolute top-0 bottom-0 left-0 right-0 z-30 flex flex-col items-center justify-center w-full h-full gap-2 bg-surface-raised"
      >
        <fluent-icon icon="cloud-backup" size="40" />
        <h4 class="text-lg font-semibold break-words text-muted">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </transition>
  </form>
</template>

<style scoped lang="scss">
.conversation--form {
  @apply pt-4 px-8 pb-8;
}

.message-editor {
  @apply px-3;

  ::v-deep {
    .rich-text-toolbar {
      @apply rounded-tl-md;
    }
  }
}

.file-uploads {
  @apply text-start;
}

.multiselect-wrap--small.has-multi-select-error {
  ::v-deep {
    .multiselect__tags {
      @apply border-red-500;
    }
  }
}

::v-deep {
  .mention--box {
    @apply left-0 m-auto right-0 top-auto h-fit;
  }

  .multiselect .multiselect__content .multiselect__option span {
    @apply inline-flex w-6 text-subtle;
  }

  .multiselect .multiselect__content .multiselect__option {
    @apply py-0.5 px-1;
  }
}
</style>
